import { useState } from "react";
import { Download, Loader2, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, download, errMsg, money } from "@/lib/api";
import { CalculTalPanel } from "@/components/CalculTalPanel";
import { ReponseAvis } from "@/components/ReponseAvis";

const num = (v) => Number(String(v).replace(",", "."));
const arrondi = (n) => Math.round(n * 100) / 100;
const jours = (iso) => Math.round((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);

const Delai = ({ fin }) => {
  const j = jours(fin);
  if (j >= 90 && j <= 183) return <p className="text-xs text-emerald-700" data-testid="avis-delai">Délai respecté : bail de 12 mois, avis à envoyer de 3 à 6 mois avant la fin ({j} jours restants).</p>;
  return <p className="text-xs text-amber-700" data-testid="avis-delai">Attention : pour un bail de 12 mois, l'avis doit être envoyé de 3 à 6 mois avant la fin du bail ({j} jours restants).</p>;
};

export const AvisRenouvellementDialog = ({ l, open, onClose, onDone }) => {
  const auj = new Date().toISOString().slice(0, 10);
  const base = l.hausse && l.hausse.a_partir_de > auj ? l.hausse.ancien : l.loyer_actuel || l.loyer_habituel;
  const [nouveau, setNouveau] = useState(l.hausse?.nouveau ?? "");
  const [pct, setPct] = useState(l.hausse ? arrondi(((l.hausse.nouveau - base) / base) * 100) : "");
  const [busy, setBusy] = useState("");
  const [tal, setTal] = useState(false);
  const setMontant = (v) => { setNouveau(v); setPct(num(v) && base ? arrondi(((num(v) - base) / base) * 100) : ""); };
  const setPourcent = (v) => { setPct(v); setNouveau(v !== "" && !Number.isNaN(num(v)) ? arrondi(base * (1 + num(v) / 100)) : ""); };
  const enregistrer = async () => {
    const n = num(nouveau);
    if (!n || Number.isNaN(n)) { toast.error("Indiquez le nouveau loyer"); return false; }
    await api.post(`/locataires/${l.id}/hausse`, { nouveau: n });
    return true;
  };
  const action = async (type) => {
    if (type === "envoi" && !window.confirm(`Envoyer l'avis par courriel à ${l.courriel} ? Une copie vous sera envoyée.`)) return;
    setBusy(type);
    try {
      if (type === "annuler") { await api.delete(`/locataires/${l.id}/hausse`); toast.success("Hausse annulée"); onDone(); return; }
      if (!(await enregistrer())) return;
      if (type === "pdf") { await download(`/locataires/${l.id}/avis-renouvellement`, {}, "avis-modification-bail.pdf"); toast.success("Nouveau loyer enregistré, avis téléchargé"); }
      else { const { data } = await api.post(`/locataires/${l.id}/avis-renouvellement/envoyer`); toast.success(`Avis envoyé à ${data.envoye_a}`); }
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="avis-dialog">
        <DialogHeader>
          <DialogTitle>Avis de modification du bail — {l.nom}</DialogTitle>
          <DialogDescription>Bail se terminant le {l.bail_fin} · loyer actuel {money(base)}. Le nouveau loyer sera suivi dans les loyers manquants dès le début du bail reconduit.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Nouveau loyer ($ / mois)</Label>
            <Input value={nouveau} onChange={(e) => setMontant(e.target.value)} inputMode="decimal" className="font-mono text-right" data-testid="avis-nouveau-loyer" /></div>
          <div className="space-y-1"><Label className="text-xs">Hausse (%)</Label>
            <Input value={pct} onChange={(e) => setPourcent(e.target.value)} inputMode="decimal" className="font-mono text-right" data-testid="avis-hausse-pct" /></div>
        </div>
        {num(nouveau) > 0 && <p className="text-xs text-slate-600" data-testid="avis-resume">Hausse de {money(num(nouveau) - base)} par mois.</p>}
        <button type="button" onClick={() => setTal(!tal)} className="text-left text-xs font-medium text-emerald-700 hover:underline" data-testid="avis-calcul-tal">{tal ? "Masquer le calcul TAL" : "Calculer la hausse recommandée (méthode du TAL)"}</button>
        {tal && <CalculTalPanel l={l} onUse={(v) => { setMontant(String(v)); setTal(false); }} />}
        {l.hausse && <ReponseAvis l={l} onDone={onDone} />}
        <Delai fin={l.bail_fin} />
        {l.hausse?.envoye_le && <p className="text-xs text-slate-500" data-testid="avis-envoye">Avis envoyé le {l.hausse.envoye_le} à {l.hausse.envoye_a}.</p>}
        <DialogFooter className="flex-wrap gap-2">
          {l.hausse && <Button variant="ghost" className="text-red-600 mr-auto" disabled={!!busy} onClick={() => action("annuler")} data-testid="avis-annuler-hausse"><Trash2 className="h-4 w-4 mr-1" />Annuler la hausse</Button>}
          <Button variant="outline" disabled={!!busy || !l.courriel} title={l.courriel ? "" : "Indiquez d'abord le courriel du locataire"} onClick={() => action("envoi")} data-testid="avis-envoyer">
            {busy === "envoi" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}Envoyer au locataire
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" disabled={!!busy} onClick={() => action("pdf")} data-testid="avis-telecharger">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Enregistrer et télécharger le PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
